import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { trpc, type RouterOutputs } from "@/lib/trpc";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import type { ErrorTableColumn } from "../bulk-stock-upload/ErrorTable";
import ChangesRowShell from "./ChangesRowShell";

/**
 * Expanded row of an applied Products bulk edit in Logs > Adjustments: every
 * value it saved, one line per SKU and field, with the old and new value.
 * The states and the table (search, the full-set CSV download) are
 * ChangesRowShell's. A Default cost carries its own
 * currency per side ("22.50 AUD" before, "22.50 USD" after), never converted;
 * a null currency, and a legacy element written before costs carried one, use
 * the org code. Size and color are text.
 */

type Change = RouterOutputs["logs"]["getSkuEditChanges"][number];

const FIELD_LABEL: Record<Change["field"], string> = {
  cost: "Default cost",
  size: "Size",
  color: "Color",
};

const SkuEditChangesRow = ({ batchId }: { batchId: string }) => {
  const { t } = useTranslation("inventory");
  const { formatWithCode } = useCurrencyFormatter();
  // An applied edit never changes, so the list is fetched once per batch.
  const changesQuery = trpc.logs.getSkuEditChanges.useQuery(
    { batchId },
    { staleTime: Infinity }
  );

  const columns = useMemo<ErrorTableColumn<Change>[]>(() => {
    const show = (c: Change, side: "from" | "to") =>
      c.field === "cost"
        ? formatWithCode(c[side] as number, side === "from" ? c.fromCurrency : c.toCurrency)
        : (c[side] ?? "");
    return [
      {
        key: "sku",
        header: "SKU",
        cellClassName: "min-w-0 break-all font-mono",
        cell: (c) => c.sku,
        csv: (c) => c.sku,
        search: (c) => c.sku,
      },
      {
        key: "field",
        header: "Field",
        className: "w-28",
        cell: (c) => FIELD_LABEL[c.field],
        csv: (c) => FIELD_LABEL[c.field],
        search: (c) => FIELD_LABEL[c.field],
      },
      {
        key: "from",
        header: "Before",
        className: "w-32 text-right",
        cellClassName: "min-w-0 break-all tabular-nums text-muted-foreground",
        cell: (c) => (c.from === null ? "Not set" : show(c, "from")),
        csv: (c) => (c.from === null ? "" : show(c, "from")),
      },
      {
        key: "to",
        header: "After",
        className: "w-32 text-right",
        cellClassName: "min-w-0 break-all tabular-nums",
        cell: (c) => show(c, "to"),
        csv: (c) => show(c, "to"),
      },
    ];
  }, [formatWithCode]);

  return (
    <ChangesRowShell<Change>
      query={changesQuery}
      columns={columns}
      rowKey={(c) => `${c.skuId}:${c.field}`}
      filename={`sku-edit-changes-${batchId}.csv`}
      searchPlaceholder={t("skuBulkEdit.logs.search")}
      texts={{
        error: t("skuBulkEdit.logs.error"),
        retry: t("common:retry"),
        empty: t("skuBulkEdit.logs.empty"),
        more: ({ shown }) => t("skuBulkEdit.logs.more", { shown }),
      }}
    />
  );
};

export default SkuEditChangesRow;
